import IconButton from '@mui/material/IconButton';
import { IconType, KioskType } from '@types';
import MobileBanner from 'components/mobileBanner/MobileBanner';
import { CallToActionButton } from 'components/shared/callToActionButton/CallToActionButton';
import Icon from 'components/shared/icon/Icon';
import Logo from 'components/shared/logo/Logo';
import { AppContext } from 'context/app';
import useIsMobile from 'hooks/useIsMobile';
import { useKioskManager } from 'hooks/useKioskManager';
import { useNetworkManager } from 'hooks/useNetworkManager';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { ROUTES } from 'utils/routes';

import styles from './Header.module.scss';

const Header: React.FC = () => {
  const { showPrintDirectionView, tutorialStep, setTutorialStep } =
    useContext(AppContext);
  const { siteName, siteLogo } = useNetworkManager();
  const { kioskMode, kiosk } = useKioskManager();
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const toggleMenu = () => {
    if (!menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    setMenuOpen(!menuOpen);
  };

  const linkClassName = (isActive: boolean) =>
    `${styles.link} ${isActive ? styles.active : ''}`;

  useEffect(
    () => () => {
      document.body.style.overflow = '';
    },
    [],
  );

  const startTutorial = () => {
    const location = window.location.pathname;
    if (
      // tutorial is active but you've gotten lost
      !tutorialStep ||
      (tutorialStep === 1 && location !== ROUTES.CAMPUS_NAVIGATION) ||
      (tutorialStep === 2 && location !== ROUTES.CAMPUS_DIRECTORY)
    ) {
      // restart tutorial
      setTutorialStep(1);
      if (location !== ROUTES.CAMPUS_NAVIGATION) {
        navigate(ROUTES.CAMPUS_NAVIGATION);
      }
    }
  };

  const renderCallToActionButton = useMemo(() => {
    const location = window.location.pathname;
    if (
      kiosk?.model === KioskType.ELO_TOUCH &&
      location === ROUTES.CAMPUS_NAVIGATION
    ) {
      return null;
    }
    return <CallToActionButton origin="Header" />;
  }, [kiosk]);

  return (
    <div className={styles.container}>
      {isMobile && (
        <div style={{ padding: '16px 16px 0 16px' }}>
          <MobileBanner />
          <div className={styles.hr}></div>
        </div>
      )}
      <div
        className={`${kiosk && kioskMode ? styles[kiosk.model] : styles.header}
        ${showPrintDirectionView && styles.hide}
      `}
      >
        <div className={styles.brandLinkContainer}>
          <div className={styles.branding}>
            <div className={styles.logo}>
              <Logo imageURL={siteLogo} companyName={siteName} />
            </div>
          </div>
          {!kioskMode && !menuOpen && (
            <div className={styles.links}>
              <NavLink
                to={{
                  pathname: ROUTES.CAMPUS_NAVIGATION,
                  search: window.location.search,
                }}
                className={({ isActive }) => linkClassName(isActive)}
              >
                {t('header.campusNavigation')}
              </NavLink>
              <NavLink
                to={{
                  pathname: ROUTES.CAMPUS_DIRECTORY,
                  search: window.location.search,
                }}
                className={({ isActive }) => linkClassName(isActive)}
              >
                {t('header.campusDirectory')}
              </NavLink>
            </div>
          )}
        </div>
        <div className={styles.buttonsContainer}>
          <div className={styles.actionButtons}>
            {renderCallToActionButton}
            {!kioskMode && !isMobile && (
              <IconButton onClick={startTutorial}>
                <Icon icon={IconType.INFO} size={24} ariaLabel="Information" />
              </IconButton>
            )}
          </div>
        </div>
        {!menuOpen && !kioskMode && (
          <div
            className={styles.menuIcon}
            onClick={toggleMenu}
            data-testid="menu-icon"
          >
            <Icon icon={IconType.HAMBURGER} />
          </div>
        )}
        {menuOpen && (
          <div
            className={styles.closeIcon}
            onClick={toggleMenu}
            data-testid="close-icon"
          >
            <Icon
              icon={IconType.CLOSE}
              size={16}
              ariaLabel="Close"
              ariaRole="button"
            />
          </div>
        )}
      </div>
      {menuOpen && (
        <div className={styles.menuDropdown}>
          <NavLink
            to={{
              pathname: ROUTES.CAMPUS_NAVIGATION,
              search: window.location.search,
            }}
            className={({ isActive }) => linkClassName(isActive)}
          >
            {t('header.campusNavigation')}
          </NavLink>
          <NavLink
            to={{
              pathname: ROUTES.CAMPUS_DIRECTORY,
              search: window.location.search,
            }}
            className={({ isActive }) => linkClassName(isActive)}
          >
            {t('header.campusDirectory')}
          </NavLink>
          <CallToActionButton origin="Header" />
        </div>
      )}
    </div>
  );
};

export default Header;
